import {
  add,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { KiSyokenComparisonSection } from '../../../domain/kiSyoken/types';

/**
 * 機種別商圏専用: 比較区分に応じて比較期間を変更する
 *
 * この関数は機種別商圏の特別な要件に対応するため、
 * 共通のcomparativeSectionUtilとは独立して実装されています。
 *
 * @param comparativeSection - 比較区分
 * @param currentDate - 基準日
 * @param now - 現在日時
 * @param excludeToday - 今日を除外するかどうか
 * @returns 比較期間の日付配列
 */
export const makeKiSyokenComparisonDateFromSelectedComparativeSection = (
  comparativeSection: KiSyokenComparisonSection,
  currentDate: Date,
  now: Date,
  excludeToday: boolean | undefined
): {
  ymdComparisonList: Date[];
} => {
  let startDate = currentDate;
  let endDate = currentDate;

  switch (comparativeSection) {
    case '前日': {
      endDate = add(currentDate, { days: -1 });
      startDate = endDate;
      break;
    }
    case '前週同曜日': {
      endDate = add(currentDate, { days: -7 });
      startDate = endDate;
      break;
    }
    case '前週': {
      // date-fnsのstartOfWeek/endOfWeekを使用して正確に計算
      // 前週の開始日（月曜日）と終了日（日曜日）を取得
      const lastWeek = add(currentDate, { weeks: -1 });
      startDate = startOfWeek(lastWeek, { weekStartsOn: 1 }); // 月曜日開始
      endDate = endOfWeek(lastWeek, { weekStartsOn: 1 }); // 日曜日終了
      break;
    }
    case '前月': {
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const lastMonth = add(currentDate, { months: -1 });

      // 期間が当月の場合は比較期間の期間範囲を検索期間に合わせる
      if (
        currentYear === now.getFullYear() &&
        currentMonth === now.getMonth() &&
        // 前月の日数が当月より少ない、かつ当日日付が前月最終日付以降の場合、
        // 比較期間には前月の1日から最終日付までが指定された状態とする
        now.getDate() < endOfMonth(lastMonth).getDate()
      ) {
        startDate = startOfMonth(lastMonth);
        endDate = new Date(
          lastMonth.getFullYear(),
          lastMonth.getMonth(),
          excludeToday && now.getDate() !== 1
            ? now.getDate() - 1
            : now.getDate()
        );
      }
      // 当月以外の場合は月全て
      else {
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
      }
      break;
    }
    case '前年同月': {
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const lastYear = add(currentDate, { years: -1 });

      // 期間が当月の場合は比較期間の期間範囲を検索期間に合わせる
      if (
        currentYear === now.getFullYear() &&
        currentMonth === now.getMonth()
      ) {
        startDate = startOfMonth(lastYear);
        endDate = new Date(
          lastYear.getFullYear(),
          lastYear.getMonth(),
          excludeToday && now.getDate() !== 1
            ? now.getDate() - 1
            : now.getDate()
        );
      }
      // 当月以外の場合は月全て
      else {
        startDate = startOfMonth(lastYear);
        endDate = endOfMonth(lastYear);
      }
      break;
    }
    // 機種別商圏では以下の比較区分は別の関数で処理される
    case 'カスタム':
    case '平日/土日祝比較':
    case '過去7日':
    case '過去14日':
    case '過去28日':
      // フォールバック: 主期間と同一にする
      startDate = currentDate;
      endDate = currentDate;
      break;
  }

  const ymdComparisonList = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });

  return { ymdComparisonList };
};
